import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  DropDownContainer,
  dropDownStyles,
  Field,
  FieldPanel,
  FieldsList,
  FormHeader,
  LineBreak,
  OptionBox,
  OptionsContainer,
  tooltipStyles,
} from '../styles';
import { Edit, Delete, Add, AddCircle, CloudUpload } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import {
  TableCellStyleEnum,
  SortTypeEnum,
  TextFormatEnum,
  PointOfInterestTemplateTypeEnum,
  UnitsEnum,
  PointOfInterestTemplateField,
  InputAdormentPositionEnum,
  InputAdormentIconTypeEnum,
} from '@/__generated__/graphql';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { TemplateFieldTypeEnum } from '@/types';
import { formatEnumForDisplay, upperSnakeCase } from '@/utils';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { EmptyStatePlaceholder } from '../../../Analysis/shared/EmptyStatePlaceholder';
import { DropDownField } from '../../../shared/CustomField';
import { TemplateFields } from '../shared';
import { selectedPOIType } from '../../state';
import { useAtom } from 'jotai';
import { PageLoader } from '../../../shared/PageLoader';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BulkOptionsModal } from '../BulkOptionsModal';
import { GenericDialog } from '../../../shared/GenericDialog';

export const PoiFieldsPanel = ({
  emptyForm,
  handleEmptyForm,
  selectedPoiCount,
  handleCreateTemplate,
  handleSaveTemplate,
  setIsDeleteDialogOpen,
  allPoiIcons,
}: {
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  selectedPoiCount: number;
  handleCreateTemplate: () => void;
  handleSaveTemplate: () => void;
  setIsDeleteDialogOpen: (value: boolean) => void;
  allPoiIcons: string[];
}) => {
  const [selectedField, setSelectedField] = useState<PointOfInterestTemplateField | undefined>(
    undefined
  );
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [selectedStaticField, setSelectedStaticField] = useState<string>();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [newOption, setNewOption] = useState<string>();
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [showOptionsModal, setShowOptionsModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const initialSelectedFieldRef = useRef<PointOfInterestTemplateField | undefined>(undefined);
  const [showDeleteFieldDialog, setShowDeleteFieldDialog] = useState<boolean>(false);

  // Track the selected field ID separately to maintain selection during updates
  const selectedFieldIdRef = useRef<string | undefined>(undefined);

  // Update the selected field ID reference whenever selection changes
  useEffect(() => {
    if (selectedField) {
      selectedFieldIdRef.current = selectedField.id;
    }
  }, [selectedField]);

  // Simple filtering without affecting selection
  const filteredTypeFields = useMemo(() => {
    const list = selectedType?.fields ?? [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return list;
    return list.filter((f) => (f?.name ?? '').toLowerCase().includes(q));
  }, [selectedType?.fields, searchTerm]);

  // Store initial field selection when search is cleared
  useEffect(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q && initialSelectedFieldRef.current) {
      // Only restore initial selection when search is cleared
      const original = initialSelectedFieldRef.current;
      setSelectedField(
        original.displayConfig ? original : { ...original, displayConfig: undefined }
      );
    }
  }, [searchTerm]);

  // First-time selection when search has results but no field is selected
  useEffect(() => {
    if (searchTerm.trim() && !selectedField && filteredTypeFields.length > 0) {
      const first = filteredTypeFields[0];
      setSelectedStaticField(undefined);
      setSelectedField(
        first.displayConfig
          ? first
          : {
              ...first,
              displayConfig: undefined,
            }
      );
    }
  }, [filteredTypeFields, selectedField, searchTerm]);

  const updateField = (field: PointOfInterestTemplateField | undefined) => {
    if (!field) return;

    const updatedField = field.isMeasurementToolEnabled
      ? {
          ...field,
          type: TemplateFieldTypeEnum.MeasurementPaintLength,
          ...(field.displayConfig
            ? {
                displayConfig: {
                  ...field.displayConfig,
                  inputAdorment: {
                    position: InputAdormentPositionEnum.End,
                    iconPath: null,
                    iconType: InputAdormentIconTypeEnum.MeasurementTool,
                  },
                },
              }
            : {
                displayConfig: {
                  id: '',
                  type: TableCellStyleEnum.Text,
                  isSortable: false,
                  sortType: SortTypeEnum.String,
                  textAreaRows: undefined,
                  textFormat: TextFormatEnum.None,
                  inputAdorment: {
                    position: InputAdormentPositionEnum.End,
                    iconPath: null,
                    iconType: InputAdormentIconTypeEnum.MeasurementTool,
                  },
                },
              }),
        }
      : field;

    // Store the current field ID before updating
    const currentFieldId = field.id;

    // Update the selected field state
    setSelectedField(updatedField);

    // Update the field in the selectedType
    setSelectedType((previousState) => {
      if (previousState) {
        const updatedFields =
          previousState.fields && previousState.fields.length > 0
            ? previousState.fields.map((f) => (f.id === updatedField.id ? updatedField : f))
            : [updatedField];

        return {
          ...previousState,
          fields: updatedFields,
        };
      }
    });

    // Ensure the field ID reference is updated
    selectedFieldIdRef.current = currentFieldId;
  };

  const handleRemoveOption = (option: string) => {
    if (selectedField) {
      const options = selectedField.options.filter((item) => item !== option) ?? [];
      updateField({ ...selectedField, options });
    }
  };

  const getNameFromPath = useCallback((path: string) => {
    return path.replace(/(^.*\/)|(\.svg$)/g, '');
  }, []);

  const handleDeleteField = () => {
    if (selectedField) {
      // Store the current field ID for reference
      const deletedFieldId = selectedField.id;

      setSelectedType((previousState) => {
        if (previousState) {
          // Filter out the deleted field
          const updatedFields =
            previousState.fields && previousState.fields.length > 0
              ? previousState.fields.filter((f) => f.id !== deletedFieldId)
              : [];

          // If there are remaining fields, select the first one
          if (updatedFields.length > 0) {
            const nextField = updatedFields[0];
            // Schedule selection of the next field after state update
            setTimeout(() => {
              setSelectedField(
                nextField.displayConfig ? nextField : { ...nextField, displayConfig: undefined }
              );
              // Update the field ID reference
              selectedFieldIdRef.current = nextField.id;
            }, 0);
          } else {
            // No fields left, clear selection
            setSelectedField(undefined);
            selectedFieldIdRef.current = undefined;
          }

          return {
            ...previousState,
            fields: updatedFields,
          };
        }
      });

      // Clear the initial selected field reference if it was the deleted field
      if (initialSelectedFieldRef.current?.id === deletedFieldId) {
        initialSelectedFieldRef.current = undefined;
      }

      setShowDeleteFieldDialog(false);
    }
  };

  if (!selectedType) return <PageLoader />;
  return (
    <>
      {showOptionsModal && (
        <BulkOptionsModal
          open={showOptionsModal}
          handleUpdate={(newOptions) => {
            if (selectedField) {
              updateField({
                ...selectedField,
                options: [...selectedField.options, ...newOptions],
              });
            }
          }}
          handleClose={() => {
            setShowOptionsModal(false);
          }}
        />
      )}
      {showDeleteFieldDialog && (
        <GenericDialog
          open={showDeleteFieldDialog}
          onClose={() => setShowDeleteFieldDialog(false)}
          title="Delete Field"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this field?
            </Typography>
          }
          actions={
            <>
              <Button variant="text" onClick={() => setShowDeleteFieldDialog(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={() => handleDeleteField()}>
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
      <Box sx={{ pt: 2, pb: 2 }}>
        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search fields"
          variant="outlined"
          size="small"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: !!searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Stack paddingTop="2rem">
        <FormHeader>
          <FormHeader>
            {emptyForm || (mode === 'Edit' && selectedPoiCount === 0) ? (
              <TemplateFields
                value={selectedType.name || ''}
                placeholder="Template Name"
                mode="Edit"
                onChange={(value) => {
                  setSelectedType({ ...selectedType, name: value });
                }}
              />
            ) : (
              <Typography fontSize="2.2rem" fontWeight={500}>
                {selectedType.name}
              </Typography>
            )}
          </FormHeader>

          {mode === 'Edit' || emptyForm ? (
            <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
              <Button
                variant="contained"
                sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                onClick={() => {
                  if (emptyForm) {
                    handleEmptyForm(false);
                    handleCreateTemplate();
                  } else {
                    setMode('ReadOnly');
                    handleSaveTemplate();
                  }
                }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                onClick={() => {
                  setMode('ReadOnly');
                  handleEmptyForm(false);
                }}
              >
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
              <Tooltip
                title="Edit Template"
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    sx: { tooltipStyles },
                  },
                }}
              >
                <IconButton size="medium" onClick={() => setMode('Edit')}>
                  <Edit
                    fontSize="inherit"
                    sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip
                title="Delete Template"
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    sx: { tooltipStyles },
                  },
                }}
              >
                <IconButton size="medium" onClick={() => setIsDeleteDialogOpen(true)}>
                  <Delete
                    fontSize="inherit"
                    sx={{ cursor: 'pointer', color: abyssColors.error[700] }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </FormHeader>

        <LineBreak marginBottom={2} />
        <Stack direction="row" width="100%" gap={2}>
          <FieldsList>
            <Stack gap={1}>
              <Field
                selected={selectedStaticField === 'Template Type'}
                onClick={() => {
                  setSelectedStaticField('Template Type');
                  setSelectedField(undefined);
                }}
              >
                Template Type
              </Field>
              <Field
                selected={selectedStaticField === 'Icon'}
                onClick={() => {
                  setSelectedStaticField('Icon');
                  setSelectedField(undefined);
                }}
              >
                Icon
              </Field>
              <Field
                selected={selectedStaticField === 'Legend Color'}
                onClick={() => {
                  setSelectedStaticField('Legend Color');
                  setSelectedField(undefined);
                  setShowColorPicker(!showColorPicker);
                }}
              >
                Legend Color
              </Field>
              {filteredTypeFields?.map((field) => (
                <Field
                  selected={selectedField?.id === field.id}
                  key={field.id}
                  onClick={() => {
                    setSelectedStaticField(undefined);
                    setSelectedField(
                      field.displayConfig
                        ? field
                        : {
                            ...field,
                            // inserting dummy object for saving values, should not make a difference
                            displayConfig: undefined,
                          }
                    );
                  }}
                >
                  {field.name}
                </Field>
              ))}
            </Stack>
            {(mode === 'Edit' || emptyForm) && (
              <Button
                sx={{ marginTop: 2, height: '40px' }}
                startIcon={<Add />}
                variant="outlined"
                size="small"
                onClick={() => {
                  const newField = {
                    name: '',
                    key: '',
                    options: [],
                    type: TemplateFieldTypeEnum.Input,
                    isReadOnly: true,
                    isRequired: true,
                    serverSide: true,
                    isMeasurementToolEnabled: false,
                    id: generateMongoObjectId(),
                    displayConfig: {
                      id: generateMongoObjectId(),
                      type: TableCellStyleEnum.Text,
                      isSortable: false,
                      sortType: SortTypeEnum.String,
                      textAreaRows: undefined,
                      textFormat: TextFormatEnum.None,
                    },
                  };
                  setSelectedType({
                    ...selectedType,
                    fields:
                      selectedType.fields && selectedType.fields?.length > 0
                        ? [...selectedType.fields, newField]
                        : [newField],
                  });
                  setSelectedField(newField);
                }}
              >
                Add Field
              </Button>
            )}
          </FieldsList>
          {!selectedField && selectedStaticField && (
            <FieldPanel>
              <Typography fontWeight={500} fontSize="1.8rem">
                {selectedStaticField}
              </Typography>
              {selectedStaticField === 'Template Type' && (
                <DropDownContainer sx={{ pt: 2 }}>
                  <Typography>Template Type</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedStaticField}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={selectedType?.type ?? PointOfInterestTemplateTypeEnum.PointOfInterest}
                    options={Object.values(PointOfInterestTemplateTypeEnum).map((value) => value)}
                    onFieldValueChange={(value) => {
                      if (value) {
                        setSelectedType({
                          ...selectedType,
                          type: value as PointOfInterestTemplateTypeEnum,
                        });
                      }
                    }}
                  />
                </DropDownContainer>
              )}
              {selectedStaticField === 'Icon' && (
                <Stack pt={2}>
                  <Stack direction="row" gap={0}>
                    <DropDownContainer>
                      <Typography>Icon</Typography>
                      <Autocomplete
                        id={selectedType?.icon?.name}
                        disableClearable
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        options={allPoiIcons.map((path) => (path ? getNameFromPath(path) : ''))}
                        value={
                          selectedType?.icon?.name
                            ? getNameFromPath(selectedType.icon.name)
                            : NOT_APPLICABLE_LABEL
                        }
                        onChange={(_, value) => {
                          if (value) {
                            setSelectedType((prev) => {
                              if (!prev) return prev;
                              return {
                                ...prev,
                                icon: {
                                  ...(prev.icon ?? {}),
                                  name: `common/poi-icons/${value}.svg`,
                                },
                              };
                            });
                          }
                        }}
                        getOptionLabel={(option) => option}
                        sx={{
                          ...dropDownStyles,
                          width: '100%',
                        }}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth variant="standard" placeholder="" />
                        )}
                      />
                    </DropDownContainer>
                    {selectedType.icon?.name && selectedType.icon?.name !== '' && (
                      <img
                        src={getCloudfrontUrl(selectedType?.icon?.name)}
                        crossOrigin="use-credentials"
                        alt="poi-icon"
                      />
                    )}
                    {selectedType.icon?.name === '' && <AbyssIcon iconName="unknown" />}
                  </Stack>
                  <TemplateFields
                    label="POI Color"
                    value={selectedType?.icon?.color ?? NOT_APPLICABLE_LABEL}
                    endAdornment="color"
                    onChange={(value) => {
                      if (value) {
                        setSelectedType((previousState) => {
                          if (previousState) {
                            return {
                              ...previousState,
                              ...(previousState.icon
                                ? { icon: { ...previousState.icon, color: value.toUpperCase() } }
                                : { icon: { name: '', color: '' } }),
                            };
                          }
                        });
                      }
                    }}
                    mode={emptyForm ? 'Edit' : mode}
                  />
                </Stack>
              )}
              {selectedStaticField === 'Legend Color' && (
                <Stack pt={2}>
                  <TemplateFields
                    label="Color"
                    value={selectedType?.accentColor ?? '#8FCE00'}
                    onChange={(value) => {
                      if (value) {
                        setSelectedType({ ...selectedType, accentColor: value.toUpperCase() });
                      }
                    }}
                    endAdornment="color"
                    mode={emptyForm ? 'Edit' : mode}
                  />
                </Stack>
              )}
            </FieldPanel>
          )}
          {selectedField ? (
            <FieldPanel gap={3} direction="row">
              <Stack width={'50%'} gap={1}>
                {mode === 'ReadOnly' && (
                  <Typography fontWeight={500} fontSize="1.8rem">
                    {selectedField.name}
                  </Typography>
                )}

                <Grid container maxWidth="60rem" ml={'-1.5rem'}>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.isRequired ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({
                            ...selectedField,
                            isRequired: event.target.checked,
                          });
                        }}
                      />
                      <Typography>Required</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Tooltip
                        title="Field Type will be updated to 'MEASUREMENT_PAINT_LENGTH' to enable Measurement Tool"
                        arrow
                        placement="top"
                        sx={{ tooltipStyles }}
                      >
                        <span>
                          <Checkbox
                            checked={selectedField.isMeasurementToolEnabled ?? false}
                            disabled={mode === 'ReadOnly' && !emptyForm}
                            onChange={(event) => {
                              updateField({
                                ...selectedField,
                                isMeasurementToolEnabled: event.target.checked,
                              });
                            }}
                          />
                        </span>
                      </Tooltip>
                      <Typography>Enable Measurement Tool</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.defaultVisible ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, defaultVisible: event.target.checked });
                        }}
                      />
                      <Typography>Default Visibility</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.isHidden ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, isHidden: event.target.checked });
                        }}
                      />
                      <Typography>Hidden</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.isReadOnly ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, isReadOnly: event.target.checked });
                        }}
                      />
                      <Typography>Read Only</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.serverSide}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({ ...selectedField, serverSide: event.target.checked });
                        }}
                      />
                      <Typography>Server-side</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.displayConfig?.isSortable}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({
                            ...selectedField,
                            ...(selectedField.displayConfig
                              ? {
                                  displayConfig: {
                                    ...selectedField.displayConfig,
                                    isSortable: event.target.checked,
                                  },
                                }
                              : {
                                  displayConfig: {
                                    id: generateMongoObjectId(),
                                    type: TableCellStyleEnum.Text,
                                    isSortable: event.target.checked,
                                    sortType: SortTypeEnum.String,
                                    textAreaRows: undefined,
                                    textFormat: TextFormatEnum.None,
                                  },
                                }),
                          });
                        }}
                      />
                      <Typography>Sortable</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.displayInReview ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({
                            ...selectedField,
                            displayInReview: event.target.checked,
                          });
                        }}
                      />
                      <Typography>Display in Review</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={selectedField.displayInReports ?? false}
                        disabled={mode === 'ReadOnly' && !emptyForm}
                        onChange={(event) => {
                          updateField({
                            ...selectedField,
                            displayInReports: event.target.checked,
                          });
                        }}
                      />
                      <Typography>Display in Reports</Typography>
                    </Stack>
                  </Grid>
                </Grid>
                {(mode === 'Edit' || emptyForm) && (
                  <TemplateFields
                    label="Name"
                    value={selectedField.name}
                    mode={emptyForm ? 'Edit' : mode}
                    onChange={(value) => {
                      updateField({
                        ...selectedField,
                        name: value,
                      });
                    }}
                  />
                )}
                <DropDownContainer>
                  <Typography>Field Type</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedField.id}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={
                      selectedField.isMeasurementToolEnabled
                        ? formatEnumForDisplay(TemplateFieldTypeEnum.MeasurementPaintLength)
                        : formatEnumForDisplay(selectedField.type) || ''
                    }
                    options={Object.values(TemplateFieldTypeEnum).map((value) =>
                      formatEnumForDisplay(value)
                    )}
                    onFieldValueChange={(value) => {
                      if (value && selectedField) {
                        const type =
                          Object.values(TemplateFieldTypeEnum).find(
                            (enumValue) => enumValue === upperSnakeCase(value)
                          ) ?? TemplateFieldTypeEnum.Input;
                        updateField({
                          ...selectedField,
                          type: type,
                        });
                      }
                    }}
                  />
                </DropDownContainer>
                {selectedField.displayConfig?.isSortable && (
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    width="100%"
                    maxWidth="50rem"
                    justifyContent="space-between"
                  >
                    <Typography>Sort Type</Typography>
                    <DropDownField
                      mode={emptyForm ? 'Edit' : mode}
                      id={selectedField.id}
                      title=""
                      sx={dropDownStyles}
                      disableClearable
                      value={formatEnumForDisplay(selectedField.displayConfig.sortType) || ''}
                      options={Object.values(SortTypeEnum).map((value) =>
                        formatEnumForDisplay(value)
                      )}
                      onFieldValueChange={(value) => {
                        if (value) {
                          const sortType = Object.values(SortTypeEnum).find(
                            (enumValue) => enumValue === upperSnakeCase(value)
                          );
                          updateField({
                            ...selectedField,
                            ...(selectedField.displayConfig
                              ? {
                                  displayConfig: {
                                    ...selectedField.displayConfig,
                                    ...(sortType
                                      ? { sortType }
                                      : { sortType: SortTypeEnum.String }),
                                  },
                                }
                              : {
                                  displayConfig: {
                                    id: generateMongoObjectId(),
                                    type: TableCellStyleEnum.Text,
                                    isSortable: false,
                                    ...(sortType
                                      ? { sortType }
                                      : { sortType: SortTypeEnum.String }),
                                    textAreaRows: undefined,
                                  },
                                }),
                          });
                        }
                      }}
                    />
                  </Stack>
                )}

                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  width="100%"
                  maxWidth="50rem"
                  justifyContent="space-between"
                >
                  <Typography>Text Format</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedField.id}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={formatEnumForDisplay(selectedField.displayConfig?.textFormat) || ''}
                    options={Object.values(TextFormatEnum).map((value) =>
                      formatEnumForDisplay(value)
                    )}
                    onFieldValueChange={(value) => {
                      if (value) {
                        const textFormat = Object.values(TextFormatEnum).find(
                          (enumValue) => enumValue === upperSnakeCase(value)
                        );
                        updateField({
                          ...selectedField,
                          ...(selectedField.displayConfig
                            ? {
                                displayConfig: {
                                  ...selectedField.displayConfig,
                                  ...(textFormat
                                    ? { textFormat }
                                    : { textFormat: TextFormatEnum.None }),
                                },
                              }
                            : {
                                displayConfig: {
                                  id: generateMongoObjectId(),
                                  type: TableCellStyleEnum.Text,
                                  isSortable: false,
                                  sortType: SortTypeEnum.String,
                                  ...(textFormat
                                    ? { textFormat }
                                    : { textFormat: TextFormatEnum.None }),
                                  textAreaRows: undefined,
                                },
                              }),
                        });
                      }
                    }}
                  />
                </Stack>

                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  width="100%"
                  maxWidth="50rem"
                  justifyContent="space-between"
                >
                  <Typography>Unit</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedField.id}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={formatEnumForDisplay(selectedField.unit) || ''}
                    options={Object.values(UnitsEnum).map((value) => formatEnumForDisplay(value))}
                    onFieldValueChange={(value) => {
                      if (value) {
                        const unit = Object.values(UnitsEnum).find(
                          (enumValue) => enumValue === upperSnakeCase(value)
                        );
                        updateField({
                          ...selectedField,
                          unit,
                        });
                      }
                    }}
                  />
                </Stack>

                {selectedField.type === TemplateFieldTypeEnum.Textarea && (
                  <TemplateFields
                    label="Text Area Rows"
                    value={selectedField.displayConfig?.textAreaRows?.toString()}
                    mode={emptyForm ? 'Edit' : mode}
                    type={TemplateFieldTypeEnum.Numeric}
                  />
                )}
                <DropDownContainer>
                  <Typography>Display Type</Typography>
                  <DropDownField
                    mode={emptyForm ? 'Edit' : mode}
                    id={selectedField.id}
                    title=""
                    sx={dropDownStyles}
                    disableClearable
                    value={formatEnumForDisplay(selectedField.displayConfig?.type) || ''}
                    options={Object.values(TableCellStyleEnum).map((value) =>
                      formatEnumForDisplay(value)
                    )}
                    onFieldValueChange={(value) => {
                      if (value) {
                        const type = Object.values(TableCellStyleEnum).find(
                          (enumValue) => enumValue === upperSnakeCase(value)
                        );
                        updateField({
                          ...selectedField,
                          ...(selectedField.displayConfig
                            ? {
                                displayConfig: {
                                  ...selectedField.displayConfig,
                                  ...(type ? { type } : { type: TableCellStyleEnum.Text }),
                                },
                              }
                            : {
                                displayConfig: {
                                  id: generateMongoObjectId(),
                                  ...(type ? { type } : { type: TableCellStyleEnum.Text }),
                                  isSortable: false,
                                  sortType: SortTypeEnum.String,
                                  inputAdorment: undefined,
                                  textAreaRows: undefined,
                                  textFormat: TextFormatEnum.None,
                                },
                              }),
                        });
                      }
                    }}
                  />
                </DropDownContainer>

                <LineBreak marginY={4} />
                {(mode === 'Edit' || emptyForm) && (
                  <Button
                    sx={{ width: '50%', height: '40px' }}
                    startIcon={<Delete />}
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setShowDeleteFieldDialog(true);
                    }}
                  >
                    Remove Field
                  </Button>
                )}
              </Stack>
              {(selectedField.type === TemplateFieldTypeEnum.MultiSelect ||
                selectedField.type === TemplateFieldTypeEnum.SingleSelect ||
                selectedField.type === TemplateFieldTypeEnum.RadioButton) && (
                <Stack sx={{ width: '50%' }} gap={2}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography fontWeight={500} fontSize="1.6rem">
                      Options
                    </Typography>
                    <Typography variant="subtitle2">({selectedField.options.length})</Typography>
                  </Stack>
                  <OptionsContainer>
                    {selectedField.options.map((option, index) => (
                      <OptionBox key={`${option}-${index}`}>
                        <Typography>{option}</Typography>
                        {(mode === 'Edit' || emptyForm) && (
                          <Delete
                            onClick={() => {
                              handleRemoveOption(option);
                            }}
                          />
                        )}
                      </OptionBox>
                    ))}
                  </OptionsContainer>
                  {(mode === 'Edit' || emptyForm) && (
                    <>
                      <Stack direction="row" gap={2} marginTop={2}>
                        <TemplateFields
                          label="Field option"
                          value={newOption}
                          onChange={(value) => {
                            if (value) {
                              setNewOption(value);
                            }
                          }}
                          mode={emptyForm ? 'Edit' : mode}
                        />
                        <Button
                          sx={{ width: '50%', height: '40px' }}
                          startIcon={<AddCircle />}
                          variant="outlined"
                          size="small"
                          disabled={!newOption}
                          onClick={() => {
                            if (newOption) {
                              updateField({
                                ...selectedField,
                                options: [...selectedField.options, newOption],
                              });
                            }
                            setNewOption('');
                          }}
                        >
                          Add New Option
                        </Button>
                      </Stack>
                      <Button
                        sx={{ width: '50%', height: '40px', ml: 'auto' }}
                        startIcon={<CloudUpload />}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setShowOptionsModal(true);
                        }}
                      >
                        Add options from file
                      </Button>
                    </>
                  )}
                </Stack>
              )}
            </FieldPanel>
          ) : (
            !selectedStaticField && (
              <FieldPanel justifyContent="center" alignItems="center">
                <EmptyStatePlaceholder
                  type="poiTable"
                  message="Click on any field in the left panel to see its information"
                />
              </FieldPanel>
            )
          )}
        </Stack>
      </Stack>
    </>
  );
};
